import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, LoadingOverlay, Text } from "@mantine/core";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { useResponsive } from "../../../context/ResponsiveContext";
import { useSalon } from "../../../context/SalonContext";
import { useAuth } from "../../../context/AuthContext";
import usePermissions from "../../../hooks/usePermissions";
import {
  useConfirmLeave,
  useGuardedNavigate,
  useIsNavigationDirty,
} from "../../../context/NavigationGuardContext";
import { SalonReport } from "../../../types/reports";
import { SalonContextType } from "../../../types/context";
import AddReportModal from "./AddReportModal";
import RemoveReportModal from "./RemoveReportModal";
import ShareReportModal from "./ShareReportModal";
import ReportsSidebar from "./ReportsSidebar";
import {
  CatalogEntry,
  catalogForAccess,
  firstReportInOrder,
  newEntryCount,
  reportDisplayName,
} from "./reportCatalog";
import {
  REPORTS_BASE_PATH,
  ReportDraftSource,
  reportPath,
  ReportsShellContext,
} from "./reportsShellContext";
import { useReportShares } from "./useReportShares";
import { useReportsList } from "./useReportsList";
import styles from "./ReportsShell.module.css";

// Context, hooks and paths live in reportsShellContext; re-exported so
// existing imports from "./ReportsShell" keep working.
export {
  REPORTS_BASE_PATH,
  reportPath,
  useReportDraftSource,
  useReportsShell,
} from "./reportsShellContext";
export type {
  ReportDraftSource,
  ReportsShellContext,
} from "./reportsShellContext";

/**
 * Reports v2 ("Reports & Summaries") shell: the personal report sidebar
 * (which carries the page title, so no header bar takes vertical space) and
 * an <Outlet> for the selected report. It is the parent route of
 * /admin/reports and /admin/reports/:reportId, so the sidebar stays mounted
 * while switching reports. Data and list mutations live in `useReportsList`;
 * this component owns the UI state and navigation.
 *
 * On a phone (the app's mobile breakpoint) there is no sidebar: the index
 * route is the full-screen report list and each report gets the screen, with
 * a back link to the list (see ReportsIndexPage / ReportInstancePage).
 *
 * Every move to another report — sidebar, Add, Duplicate — goes through the
 * navigation guard, so a canvas with an unsaved view asks Save / Discard
 * changes first. (Duplicate with a registered draft carries the draft to
 * the copy instead, so there's nothing to ask about.)
 */
export default function ReportsShell() {
  const {
    businessInfo,
    currentUser,
  }: Pick<SalonContextType, "businessInfo" | "currentUser"> = useSalon();
  const salonId = businessInfo?.id;
  // The signed-in Cognito user is known on the first render, while
  // SalonContext's currentUser waits for the staff list. Keying "last viewed"
  // on it means the index redirect never runs before the user is known (it
  // used to fall back to the first report and then save that over the
  // remembered one).
  const { currentUser: signedInUser } = useAuth() as {
    currentUser?: { userId?: string } | null;
  };
  const userId =
    signedInUser?.userId ?? (currentUser?.id ? String(currentUser.id) : null);
  const { reportId: activeReportId = null } = useParams<{
    reportId: string;
  }>();
  const navigate = useGuardedNavigate();
  // Only for leaving a report that was just removed: its unsaved view went
  // with it, so asking to save it would be wrong.
  const navigateUnguarded = useNavigate();
  const confirmLeave = useConfirmLeave();
  const navigationDirty = useIsNavigationDirty();
  const { isMobile } = useResponsive();
  const list = useReportsList(salonId);
  const { catalog, reports } = list;
  const perms = usePermissions();
  const addableCatalog = useMemo(
    () => catalogForAccess(catalog, perms?.reports),
    [catalog, perms?.reports],
  );
  // Same catalog as the picker, so the pill equals its What's new count.
  const newReportCount = newEntryCount(addableCatalog);

  const [addOpen, setAddOpen] = useState(false);
  const [addingType, setAddingType] = useState<string | null>(null);
  const [pendingRemove, setPendingRemove] = useState<SalonReport | null>(null);
  // The report open in the Share modal (read from the list, so its share
  // count stays current).
  const [sharingReportId, setSharingReportId] = useState<string | null>(null);
  const sharingReport = reports.find((r) => r.id === sharingReportId) ?? null;
  const sharing = useReportShares(
    salonId ?? "",
    sharingReport,
    list.setReportShareCount,
  );
  const canShareReports = perms?.reports?.canShareReports === true;
  const [removingReportId, setRemovingReportId] = useState<string | null>(null);
  const [editingReportId, setEditingReportId] = useState<string | null>(null);
  const [dirtyReportIds, setDirtyReportIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const draftSources = useRef(new Map<string, ReportDraftSource>());

  // Opening a just-created report waits for the commit that inserted it (and
  // that let a canvas drop a draft that moved to the copy), then navigates
  // through the guard.
  const [openAfterCommit, setOpenAfterCommit] = useState<string | null>(null);
  useEffect(() => {
    if (!openAfterCommit) return;
    setOpenAfterCommit(null);
    navigate(reportPath(openAfterCommit));
  }, [openAfterCommit, navigate]);

  const setReportDirty = useCallback((id: string, dirty: boolean) => {
    setDirtyReportIds((prev) => {
      if (prev.has(id) === dirty) return prev;
      const next = new Set(prev);
      if (dirty) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const registerDraftSource = useCallback(
    (reportId: string, source: ReportDraftSource) => {
      draftSources.current.set(reportId, source);
      return () => {
        if (draftSources.current.get(reportId) === source) {
          draftSources.current.delete(reportId);
        }
      };
    },
    [],
  );

  const clearEditingReport = useCallback(() => setEditingReportId(null), []);
  const openAddReport = useCallback(() => setAddOpen(true), []);

  const openCreated = (report: SalonReport, editName: boolean) => {
    if (editName) setEditingReportId(report.id);
    setOpenAfterCommit(report.id);
  };

  const addEntry = async (entry: CatalogEntry) => {
    const repeat = reports.some((r) => r.report_type === entry.reportType);
    setAddingType(entry.reportType);
    const report = await list.addReport(entry);
    setAddingType(null);
    if (!report) return;
    setAddOpen(false);
    // A repeat opens name editing so the copies can be told apart.
    openCreated(report, repeat);
  };

  // Adding opens the new report, i.e. leaves the current one. With unsaved
  // changes, the catalog closes and the guard dialog resolves them first;
  // otherwise the catalog stays up showing the Add button's spinner.
  const handleAdd = (entry: CatalogEntry) => {
    if (!navigationDirty) {
      void addEntry(entry);
      return;
    }
    setAddOpen(false);
    confirmLeave(() => void addEntry(entry));
  };

  const duplicateReport = (report: SalonReport) => {
    const source = draftSources.current.get(report.id);
    const draft = source?.getDraftViewConfig() ?? null;
    const run = async () => {
      const copy = await list.duplicateReport(report, draft);
      if (!copy) return;
      // The draft now lives on the copy; the original reverts, so the guarded
      // navigation below finds nothing unsaved.
      if (draft) source?.discardDraft();
      openCreated(copy, true);
    };
    if (draft) void run();
    else confirmLeave(() => void run());
  };

  const confirmRemove = async (deleteSharedCopies: boolean) => {
    if (!pendingRemove) return;
    const removed = pendingRemove;
    setRemovingReportId(removed.id);
    const rest = await list.removeReport(removed, { deleteSharedCopies });
    setRemovingReportId(null);
    if (!rest) return;
    setPendingRemove(null);
    setReportDirty(removed.id, false);
    if (activeReportId === removed.id) {
      // Phones go back to the list; desktop opens the next report.
      const next = isMobile ? null : firstReportInOrder(rest, catalog);
      navigateUnguarded(next ? reportPath(next.id) : REPORTS_BASE_PATH, {
        replace: true,
      });
    }
  };

  if (!salonId) {
    return (
      <Box className={styles.shell} pos="relative">
        <LoadingOverlay visible />
      </Box>
    );
  }

  const context: ReportsShellContext = {
    salonId,
    userId,
    catalog,
    newReportCount,
    reports,
    isMobile,
    loading: list.loading,
    loadError: list.loadError,
    removingReportId,
    editingReportId,
    clearEditingReport,
    openAddReport,
    updateReportInList: list.updateReportInList,
    requestRemove: (report) => {
      if (!removingReportId) setPendingRemove(report);
    },
    duplicateReport,
    requestShare: canShareReports
      ? (report) => setSharingReportId(report.id)
      : null,
    saveReportMeta: list.saveReportMeta,
    setReportDirty,
    registerDraftSource,
  };

  const body = list.loadError ? (
    <Text className={styles.error}>{list.loadError}</Text>
  ) : (
    <Outlet context={context} />
  );
  const modals = (
    <>
      <AddReportModal
        opened={addOpen}
        onClose={() => setAddOpen(false)}
        catalog={addableCatalog}
        reports={reports}
        onAdd={handleAdd}
        addingType={addingType}
      />
      <RemoveReportModal
        reportName={
          pendingRemove ? reportDisplayName(pendingRemove, catalog) : null
        }
        shareCount={pendingRemove?.share_count ?? 0}
        removing={!!removingReportId}
        onCancel={() => setPendingRemove(null)}
        onConfirm={confirmRemove}
      />
      <ShareReportModal
        reportName={
          sharingReport ? reportDisplayName(sharingReport, catalog) : null
        }
        onClose={() => setSharingReportId(null)}
        sharing={sharing}
      />
    </>
  );

  // One tree for both layouts: the sidebar comes and goes with the
  // breakpoint, but the <Outlet> keeps its place, so the open report (and its
  // unsaved view) survives a tablet rotation or a window resize.
  return (
    <Box className={isMobile ? styles.mobileShell : styles.shell}>
      <Box className={styles.body}>
        {!isMobile && (
          <ReportsSidebar
            reports={reports}
            catalog={catalog}
            newCount={newReportCount}
            activeReportId={activeReportId}
            dirtyReportIds={dirtyReportIds}
            addDisabled={list.loading}
            onSelect={(report) => navigate(reportPath(report.id))}
            onAddReport={openAddReport}
          />
        )}
        <Box className={styles.main} pos="relative">
          <LoadingOverlay visible={list.loading} zIndex={2} />
          {body}
        </Box>
      </Box>
      {modals}
    </Box>
  );
}
